/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { filter } from 'lodash';
import prettyBytes from 'pretty-bytes';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import AssistantIcon from '@mui/icons-material/Assistant';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import { Alert, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';

import { DefaultClusterLimits } from 'shared';

import { AlignCenter } from '../../components/AlignCenter/AlignCenter';
import { Boop } from '../../components/Boop/component';
import { OverlayFormWithControls } from '../../components/OverlayFormWithControls/OverlayFormWithControls';
import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { StepsIndicator } from '../../components/StepsIndicator/StepsIndicator';
import { BasicRoutes } from '../../constants/basicRoutes';
import { UnitsToBytesMultiplier } from '../../elements/HUBytesInput/components/UnitPicker.types';
import { Namespaces } from '../../i18n';
import { ClusterApiService } from '../../services/ClusterApiService';
import { RootState } from '../../store';
import { enqueueSnackbar } from '../../store/notifier/actions';
import { useCreateEntityOverlayStyles } from '../../styles/createEntityOverlay';
import { ApiClientError } from '../../types/api/ApiClientError';
import { ApiClientResponse } from '../../types/api/ApiClientResponse';
import { ClusterConfiguration } from '../../types/cluster/ClusterConfiguration';
import { CreateClusterResponseBody } from '../../types/cluster/CreateClusterResponseBody';
import { ClusterAllocationMode } from '../../types/common/AllocationMode';
import { SystemConfiguration } from '../../types/superuser/SystemConfiguration';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import {
  fetchCluster,
  fetchDatasets,
  fetchPostgresVersions,
  fetchStorageClasses,
  fetchSystemConfiguration,
} from '../../utils/queryFns';
import { reactToCreateCluster, reactToCreateClusterFromBackup } from '../../utils/reactions';
import { allocationMode } from '../../utils/storageHelpers';
import { useApiValidation } from '../../utils/useApiValidation';
import { useObjectState } from '../../utils/useObjectState';
import { CreateClusterStepBackup } from './components/CreateClusterStepBackup';
import { CreateClusterStepConfigureBackup } from './components/CreateClusterStepConfigureBackup';
import { CreateClusterStepConfigureMaintenanceWindows } from './components/CreateClusterStepConfigureMaintenanceWindows';
import { CreateClusterStepDataSets } from './components/CreateClusterStepDataSets';
import { CreateClusterStepInfo } from './components/CreateClusterStepInfo';
import { CreateClusterStepSettings } from './components/CreateClusterStepSettings';
import { CreateClusterStepSummary } from './components/CreateClusterStepSummary';
import { CreateClusterStepSupportPlan } from './components/CreateClusterStepSupportPlan';
import { AllocationContext, PlansContext, ReplicaContext } from './components/forms/consts';
import { TAllocationContext, TPlansContext, TReplicaContext } from './components/forms/types';
import {
  createClusterBackupConfigureStepValidationSchema,
  createClusterBackupStepValidationSchema,
  createClusterInfoValidationSchema,
  createClusterMaintenanceWindowsConfigureStepValidationSchema,
  formFromBackupInitialValues,
} from './CreateClusterOverlay.consts';
import {
  CreateClusterBackupSettings,
  CreateClusterConfigureBackup,
  CreateClusterConfigureMaintenanceWindows,
  CreateClusterFromBackupValues,
  CreateClusterInfo,
  CreateClusterOverlayMapType,
  CreateClusterOverlaySteps,
  CreateClusterSettings,
  FetchParamsState,
  OnClusterCreateCallback,
} from './CreateClusterOverlay.types';
import { createClusterFormFromBackupOverlayMap, createClusterRequest } from './utils';

const mapState = (state: RootState) => ({
  projects: state.projects,
  plan: state.plan,
  organizations: state.organizations,
  createCluster: state.createCluster,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type CreateClusterOverlayFormProps = PropsFromRedux & {
  onClusterCreate: OnClusterCreateCallback;
  skipDatasetStep?: boolean;
  setStickyButtons: (value: boolean) => any;
};

const infoValidationSchema = createClusterInfoValidationSchema;
const backupValidationSchema = createClusterBackupStepValidationSchema;
const backupConfigureValidationSchema = createClusterBackupConfigureStepValidationSchema;
const maintenanceWindowsConfigureValidationSchema = createClusterMaintenanceWindowsConfigureStepValidationSchema;

const getInitialValues = (data: Partial<CreateClusterFromBackupValues> = {}) => ({
  ...formFromBackupInitialValues,
  ...data,
});

type State = {
  step: CreateClusterOverlaySteps;
  stepValidationSchema: Yup.ObjectSchema<
    | CreateClusterInfo
    | CreateClusterSettings
    | CreateClusterBackupSettings
    | CreateClusterConfigureBackup
    | CreateClusterConfigureMaintenanceWindows
    | object
  >;
  isFetching: boolean;
  warnInaccessibleInstance: boolean;
  errorModal: {
    isOpen: boolean;
    errorMessage: string;
  };
};

const CreateClusterOverlayForm = memo<CreateClusterOverlayFormProps>(props => {
  const { organizations, dispatch, createCluster, projects, plan, onClusterCreate, skipDatasetStep, setStickyButtons } =
    props;
  const classes = useCreateEntityOverlayStyles();
  const { serviceType, isOpen } = createCluster;
  const { t, i18n } = useTranslation(Namespaces.CreateCluster);
  const [state, setState] = useObjectState<State>({
    isFetching: false,
    warnInaccessibleInstance: false,
    stepValidationSchema: backupValidationSchema,
    step: CreateClusterOverlaySteps.STEP_BACKUP,
    errorModal: {
      isOpen: false,
      errorMessage: '',
    },
  });
  const [fetchParams, setFetchParams] = useObjectState<FetchParamsState>({
    organizationId: createCluster.organizationId,
    projectId: createCluster.projectId,
    clusterId: createCluster.clusterId,
  });
  const { step, warnInaccessibleInstance, isFetching, stepValidationSchema, errorModal } = state;
  const { apiErrors, resetApiError, resetFieldApiError, setApiError } = useApiValidation();
  const navigate = useNavigate();

  const [mode, setMode] = useState(allocationMode.get());
  const [modeSwitchWasVisited, setModeSwitchWasVisited] = useState(allocationMode.visited()); // Using state so we don't have to query the local storage at every render

  const clusterQuery = useQuery([fetchCluster.key, fetchParams], fetchCluster.fn);

  const dataSetsCategoriesQuery = useQuery([fetchDatasets.key, { language: i18n.language }], fetchDatasets.fn, {
    refetchOnWindowFocus: false,
  });

  const { data: configData } = useQuery([fetchSystemConfiguration.key], fetchSystemConfiguration.fn, {
    refetchOnMount: true,
  });

  const storageClassesQuery = useQuery([fetchStorageClasses.key], fetchStorageClasses.fn, {
    refetchOnMount: true,
  });

  const postgresVersionsQuery = useQuery([fetchPostgresVersions.key], fetchPostgresVersions.fn, {
    refetchOnMount: true,
  });

  const cluster = useMemo(() => clusterQuery.data?.payload, [clusterQuery.data]);

  const currentConfig = useMemo(
    () =>
      cluster ? cluster.configuration.filter(conf => conf.endedAt === 'infinity')[0] : ({} as ClusterConfiguration),
    [cluster],
  );

  const config = useMemo(() => configData?.payload ?? ({} as SystemConfiguration), [configData]);

  const storageClasses = useMemo(() => storageClassesQuery.data?.data.payload ?? [], [storageClassesQuery.data]);

  const postgresVersions = useMemo(
    () => postgresVersionsQuery.data?.payload.versions ?? [],
    [postgresVersionsQuery.data],
  );

  const replicaState = useMemo(
    () => ({
      replicaMin: config.minReplicasLimit ?? DefaultClusterLimits.Replicas.Min,
      replicaMax: config.maxReplicasLimit ?? DefaultClusterLimits.Replicas.Max,
    }),
    [config.maxReplicasLimit, config.minReplicasLimit],
  );

  const [plansState, setPlansState] = useObjectState({
    computeId: undefined,
    storageId: undefined,
  });

  const initialValues = useMemo<CreateClusterFromBackupValues>(() => {
    const defaultPostgresVersion = postgresVersions.at(-1);
    const defaultStorageClass = storageClasses.find(sc => sc.isDefault);
    return getInitialValues({
      projectId: createCluster.projectId,
      organizationId: createCluster.organizationId,
      backupProjectId: createCluster.projectId,
      backupOrganizationId: createCluster.organizationId,
      backupClusterId: createCluster.clusterId,
      backupTimestamp: new Date(),
      oldestRestorePoint: null,
      postgresVersion: defaultPostgresVersion,
      storageClassId: defaultStorageClass?.id,
    });
  }, [
    postgresVersions,
    createCluster.projectId,
    createCluster.organizationId,
    createCluster.clusterId,
    storageClasses,
  ]);

  const settingsValidationSchema = useMemo(
    () =>
      Yup.object({
        cpuUnits: Yup.number().when([], {
          is: () => !!config.minCpuLimit || !!config.maxCpuLimit,
          then: () => {
            let basicSchema = Yup.number().required();

            if (config.minCpuLimit) {
              basicSchema = basicSchema.min(config.minCpuLimit);
            }

            if (config.maxCpuLimit) {
              basicSchema = basicSchema.max(config.maxCpuLimit);
            }

            return basicSchema;
          },
          otherwise: () => Yup.number().required().min(DefaultClusterLimits.Cpu.Min),
        }),
        memoryGbs: Yup.number()
          .required(t('validation.memoryGbs.empty'))
          .min(
            config.minMemoryLimit ?? DefaultClusterLimits.MemoryGbs.Min,
            t('validation.memoryGbs.min_capacity', {
              capacity: prettyBytes(
                (config.minMemoryLimit ?? DefaultClusterLimits.MemoryGbs.Min) * UnitsToBytesMultiplier['GB'],
              ),
            }),
          )
          .when([], {
            is: () => !!config.maxMemoryLimit,
            then: (schema: any) => {
              if (config.maxMemoryLimit) {
                return schema.max(
                  config.maxMemoryLimit,
                  t('validation.memoryGbs.max_capacity', {
                    capacity: prettyBytes(config.maxMemoryLimit * UnitsToBytesMultiplier['GB']),
                  }),
                );
              }
              return schema;
            },
          }),
        storageGbs: Yup.number()
          .required(t('validation.storageGbs.empty'))
          .min(
            Math.max(currentConfig.storageGbs ?? -1, config.minStorageLimit ?? DefaultClusterLimits.StorageGbs.Min),
            t('validation.storageGbs.min_capacity', {
              capacity: prettyBytes(
                Math.max(
                  currentConfig.storageGbs ?? -1,
                  config.minStorageLimit ?? DefaultClusterLimits.StorageGbs.Min,
                ) * UnitsToBytesMultiplier['GB'],
              ),
            }),
          )
          .when([], {
            is: () => !!config.maxStorageLimit,
            then: (schema: any) => {
              if (config.maxStorageLimit) {
                return schema.max(
                  config.maxStorageLimit,
                  t('validation.storageGbs.max_capacity', {
                    capacity: prettyBytes(config.maxStorageLimit * UnitsToBytesMultiplier['GB']),
                  }),
                );
              }
              return schema;
            },
          }),
        replicas: Yup.number().notOneOf([null]).required(),
        postgresVersion: Yup.number().required(),
      }),
    [
      t,
      config.minMemoryLimit,
      config.minStorageLimit,
      config.minCpuLimit,
      config.maxCpuLimit,
      config.maxMemoryLimit,
      config.maxStorageLimit,
      currentConfig.storageGbs,
    ],
  );

  const changeStep = useCallback(
    (nextStep: State['step']) => {
      const setNextStepState = (validationSchema: State['stepValidationSchema'] = Yup.object({})) => {
        setState({
          step: nextStep,
          stepValidationSchema: validationSchema,
          warnInaccessibleInstance: false,
        });
      };
      switch (nextStep) {
        case CreateClusterOverlaySteps.STEP_BACKUP:
          setNextStepState(backupValidationSchema);
          break;
        case CreateClusterOverlaySteps.STEP_INFO:
          setNextStepState(infoValidationSchema);
          break;
        case CreateClusterOverlaySteps.STEP_SETTINGS:
          // moved to formik due to weird state updates
          setNextStepState();
          break;
        case CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP:
          setNextStepState(backupConfigureValidationSchema);
          break;
        case CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS:
          setNextStepState(maintenanceWindowsConfigureValidationSchema);
          break;
        default:
          setNextStepState();
          break;
      }
    },
    [setState],
  );

  useEffect(() => {
    if (!isOpen) {
      changeStep(CreateClusterOverlaySteps.STEP_BACKUP);
    }
  }, [changeStep, isOpen]);

  const onFormCompleted = async (
    values: CreateClusterFromBackupValues,
    helpers: FormikHelpers<CreateClusterFromBackupValues>,
  ) => {
    setState({ isFetching: true });
    resetApiError();
    const { organizationId, projectId } = values;
    try {
      const cluster = createClusterRequest(values, serviceType, true);
      let result: ApiClientResponse<CreateClusterResponseBody> | undefined = undefined;
      result = await ClusterApiService.createCluster({ organizationId, projectId, data: cluster });
      void navigate(
        prepareRoutePath(BasicRoutes.CLUSTER_PAGE, {
          ':organization_id': organizationId,
          ':project_id': projectId,
          ':cluster_id': result?.data.payload.id,
        }),
      );
      void reactToCreateCluster({
        organizationId: organizationId!,
        projectId: projectId!,
      });
      void reactToCreateClusterFromBackup({
        organizationId: organizationId!,
        projectId: projectId!,
        sourceClusterId: values.backupClusterId!,
      });
      setState({ isFetching: false });
      !!result &&
        onClusterCreate(result.data.payload, {
          fromBackup: false,
          organizationId: organizationId!,
          projectId: projectId!,
        });
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error);
      const responseData = error.response?.data;
      if (!responseData) {
        setState({ isFetching: false });
        dispatch(
          enqueueSnackbar(error.response?.data.message || 'Something went wrong', {
            variant: 'error',
          }),
        );
      } else {
        const validationState = setApiError(error);
        setState({ isFetching: false });
        if (validationState.error === 'duplicate_name') {
          changeStep(CreateClusterOverlaySteps.STEP_INFO);
        }
        if (
          validationState.error === 'cluster_creation_disabled' ||
          validationState.error === 'insufficient_resources'
        ) {
          setState({
            errorModal: {
              isOpen: true,
              errorMessage: `warnings.${validationState.error}`,
            },
          });
        } else if (validationState.error === 'insufficient_space') {
          void helpers.setFieldValue('selectedDataSetIds', []);
          setState({
            errorModal: {
              isOpen: true,
              errorMessage: `${Namespaces.Common}:dataset_import_errors.${validationState.error}`,
            },
          });
          if (isDataSetsStepAvailable) {
            changeStep(CreateClusterOverlaySteps.STEP_DATA_SETS);
          }
        }
      }
    }
  };

  const isDataSetsStepAvailable = useMemo<boolean>(
    () => !skipDatasetStep && (dataSetsCategoriesQuery.data?.payload ?? []).some(i => i.datasets.length > 0),
    [dataSetsCategoriesQuery.data, skipDatasetStep],
  );

  const overlayMap = useMemo<CreateClusterOverlayMapType<CreateClusterOverlaySteps>>(() => {
    return createClusterFormFromBackupOverlayMap(isDataSetsStepAvailable);
  }, [isDataSetsStepAvailable]);

  const stepsCount = useMemo(() => {
    return filter(overlayMap, i => i.stepNumber !== null).length;
  }, [overlayMap]);

  useEffect(() => {
    setStickyButtons(step === CreateClusterOverlaySteps.STEP_DATA_SETS);
  }, [setStickyButtons, step]);

  const onStepSubmit = (
    values: CreateClusterFromBackupValues,
    actions: FormikHelpers<CreateClusterFromBackupValues>,
  ) => {
    switch (step) {
      case CreateClusterOverlaySteps.STEP_BACKUP:
        changeStep(overlayMap[step].nextStep);
        void actions.setTouched({});
        break;
      case CreateClusterOverlaySteps.STEP_INFO:
        changeStep(overlayMap[step].nextStep);
        break;
      case CreateClusterOverlaySteps.STEP_SUMMARY:
        void onFormCompleted(values, actions);
        break;
      default:
        changeStep(overlayMap[step].nextStep);
        break;
    }
  };

  const onBackClick = () => {
    changeStep(overlayMap[step].prevStep);
  };

  const isLoading =
    projects.isFetching ||
    plan.isFetching ||
    organizations.isFetching ||
    dataSetsCategoriesQuery.status === 'loading' ||
    storageClassesQuery.status === 'loading' ||
    postgresVersionsQuery.status == 'loading';

  const isError =
    (!projects.items?.length && !organizations.options.length) ||
    !plan.planCompute?.length ||
    !plan.planStorage?.length ||
    !storageClasses.length ||
    !postgresVersions.length;

  const allocationContext = useMemo<TAllocationContext>(
    () => ({
      apiErrors,
      resetApiError,
    }),
    [apiErrors, resetApiError],
  );

  const replicaContext = useMemo<TReplicaContext>(
    () => ({
      state: replicaState,
    }),
    [replicaState],
  );

  const plansContext = useMemo<TPlansContext>(
    () => ({
      selectedPlans: plansState,
      setSelectedPlans: setPlansState,
    }),
    [plansState, setPlansState],
  );

  const formValidationSchema = useMemo(
    () => (step === 1 ? settingsValidationSchema : stepValidationSchema),
    [settingsValidationSchema, step, stepValidationSchema],
  );

  useEffect(() => {
    const currentStepConfig = overlayMap[step];
    if (currentStepConfig.stepNumber === null) {
      changeStep(currentStepConfig.prevStep);
    }
  }, [changeStep, overlayMap, step]);

  const handleModeChange = () => {
    const nextMode = mode === ClusterAllocationMode.Plan ? ClusterAllocationMode.Dynamic : ClusterAllocationMode.Plan;
    allocationMode.set(nextMode);
    setMode(nextMode);
    setModeSwitchWasVisited(true);
  };

  const formRef = useRef<FormikProps<CreateClusterFromBackupValues> | null>(null);

  useEffect(() => {
    const keyListener = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && formRef.current) {
        e.preventDefault();
        void formRef.current.submitForm();
      }
    };
    document.addEventListener('keydown', keyListener, true);
    return () => {
      document.removeEventListener('keydown', keyListener);
    };
  }, []);

  const handleValidate = () => apiErrors;

  const innerRef = (f: FormikProps<CreateClusterFromBackupValues>) => {
    formRef.current = f;
  };

  const shouldDisableNextButton = (formProps: FormikProps<CreateClusterFromBackupValues>) =>
    formProps.isValidating || isFetching || postgresVersionsQuery.isError;

  return (
    <div className={classes.root}>
      {isLoading ? (
        <AlignCenter className={classes.loaderContainer}>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : !isLoading && isError ? (
        <Alert severity="error">{t('labels.unexpected_error')}</Alert>
      ) : isOpen ? (
        <div className={classes.containerInner}>
          <Typography classes={{ h2: classes.title }} variant="h2">
            {t('templates.create_cluster_from_backup', {
              name: t(`general:services.types.${serviceType}.short`),
            })}
            {step === CreateClusterOverlaySteps.STEP_SETTINGS && (
              <Boop active={!modeSwitchWasVisited}>
                <Tooltip
                  title={
                    mode === ClusterAllocationMode.Dynamic
                      ? t('labels.mode.switch_to_plan')
                      : t('labels.mode.switch_to_dynamic')
                  }
                >
                  <IconButton size="small" onClick={handleModeChange} classes={{ root: classes.assistantIcon }}>
                    {mode === ClusterAllocationMode.Dynamic ? <AssistantOutlinedIcon /> : <AssistantIcon />}
                  </IconButton>
                </Tooltip>
              </Boop>
            )}
          </Typography>
          <Formik<CreateClusterFromBackupValues>
            onSubmit={onStepSubmit}
            initialValues={initialValues}
            validationSchema={formValidationSchema}
            validateOnChange
            validateOnBlur
            validate={handleValidate}
            innerRef={innerRef}
          >
            {formProps => {
              const buttons = (
                <>
                  {step !== CreateClusterOverlaySteps.STEP_BACKUP && (
                    <Button onClick={onBackClick} variant="outlined" disabled={formProps.isValidating || isFetching}>
                      {t('buttons.previous')}
                    </Button>
                  )}
                  <Button
                    onClick={formProps.submitForm}
                    variant="contained"
                    disabled={shouldDisableNextButton(formProps)}
                  >
                    {step === CreateClusterOverlaySteps.STEP_SUMMARY ? (
                      isFetching ? (
                        <CircularProgress size={25} />
                      ) : (
                        t('buttons.create')
                      )
                    ) : (
                      t('buttons.next')
                    )}
                  </Button>
                </>
              );
              return (
                <OverlayFormWithControls
                  onSubmit={formProps.handleSubmit}
                  stepsIndicator={
                    overlayMap[step].stepNumber ? (
                      <StepsIndicator stepsCount={stepsCount} stepNumber={overlayMap[step].stepNumber!} />
                    ) : null
                  }
                  buttons={step === CreateClusterOverlaySteps.STEP_DATA_SETS ? undefined : buttons}
                >
                  {step === CreateClusterOverlaySteps.STEP_BACKUP && (
                    <CreateClusterStepBackup
                      resetFieldApiError={resetFieldApiError}
                      apiErrors={apiErrors}
                      setFetchParams={setFetchParams}
                      {...formProps}
                    />
                  )}
                  {step === CreateClusterOverlaySteps.STEP_INFO && (
                    <CreateClusterStepInfo<CreateClusterFromBackupValues>
                      {...formProps}
                      createCluster={createCluster}
                      warnInaccessibleInstance={warnInaccessibleInstance}
                      apiErrors={apiErrors}
                      resetApiError={resetFieldApiError}
                    />
                  )}
                  {step === CreateClusterOverlaySteps.STEP_SETTINGS && (
                    <AllocationContext.Provider value={allocationContext}>
                      <ReplicaContext.Provider value={replicaContext}>
                        <PlansContext.Provider value={plansContext}>
                          <CreateClusterStepSettings
                            {...(formProps as any)}
                            plan={plan}
                            serviceType={serviceType}
                            mode={mode}
                            postgresVersions={postgresVersions}
                            storageClasses={storageClasses}
                          />
                        </PlansContext.Provider>
                      </ReplicaContext.Provider>
                    </AllocationContext.Provider>
                  )}
                  {step === CreateClusterOverlaySteps.STEP_CONFIGURE_BACKUP && (
                    <CreateClusterStepConfigureBackup {...formProps} />
                  )}
                  {step === CreateClusterOverlaySteps.STEP_SUPPORT_PLAN && (
                    <CreateClusterStepSupportPlan {...formProps} />
                  )}
                  {step === CreateClusterOverlaySteps.STEP_CONFIGURE_MAINTENANCE_WINDOWS && (
                    <CreateClusterStepConfigureMaintenanceWindows {...formProps} />
                  )}
                  {step === CreateClusterOverlaySteps.STEP_DATA_SETS && (
                    <PlansContext.Provider value={plansContext}>
                      <CreateClusterStepDataSets {...formProps} plan={plan} buttons={buttons} />
                    </PlansContext.Provider>
                  )}
                  {step === CreateClusterOverlaySteps.STEP_SUMMARY && (
                    <PlansContext.Provider value={plansContext}>
                      <CreateClusterStepSummary<CreateClusterFromBackupValues>
                        {...formProps}
                        plan={plan}
                        serviceType={serviceType}
                      />
                    </PlansContext.Provider>
                  )}
                </OverlayFormWithControls>
              );
            }}
          </Formik>
          <SimpleNotifyModal
            open={errorModal.isOpen}
            message={t([errorModal.errorMessage, `${Namespaces.Common}:errors.unexpected_error`])}
            onOk={() =>
              setState({
                errorModal: {
                  isOpen: false,
                  errorMessage: '',
                },
              })
            }
          />
        </div>
      ) : null}
    </div>
  );
});

export const CreateClusterOverlayFormBackup = connector(CreateClusterOverlayForm);
